import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { getAgentListV2 } from "../../../../services/agents";

const AgentsContext = createContext();

const initialState = {
  baseAgents: [],
  baseMeta: null,
  agents: [],
  agentsMeta: null,
  loading: false,
  error: null,
};

export const AgentsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const setPartialState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchAgents = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const response = await getAgentListV2(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const agents = Array.isArray(response.data) ? response.data : [];
        const meta = response.meta || null;

        setState((prev) => ({
          ...prev,
          baseAgents: persistAsBase ? agents : prev.baseAgents,
          baseMeta: persistAsBase ? meta : prev.baseMeta,
          agents,
          agentsMeta: meta,
          loading: false,
          error: null,
        }));
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error("Error fetching agents:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          agents: [],
          agentsMeta: null,
          ...(persistAsBase
            ? {
                baseAgents: [],
                baseMeta: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const resetAgentsToBase = useCallback(() => {
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;

    setState((prev) => ({
      ...prev,
      agents: prev.baseAgents,
      agentsMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetAgents = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    baseAgents: state.baseAgents,
    baseMeta: state.baseMeta,
    agents: state.agents,
    agentsMeta: state.agentsMeta,
    loading: state.loading,
    error: state.error,
    fetchAgents,
    resetAgentsToBase,
    resetAgents,
  };

  return (
    <AgentsContext.Provider value={value}>
      {children}
    </AgentsContext.Provider>
  );
};

export const useAgents = () => {
  const context = useContext(AgentsContext);
  if (!context) {
    throw new Error('useAgents must be used within AgentsProvider');
  }
  return context;
};

